<script setup>
import { ref, computed, inject, toRaw } from 'vue'
import { useNavigationStore } from '../../../stores/NavigationStore'
import { useActionCenterStore } from '../../../stores/ActionCenterStore'
import { RequestService } from '../../../services/RequestService'
import { ScoreCalculationsService, normalizeAnswer } from '../../../services/ScoreCalculationsService'
import { buildAnswersMap, normalizeAnswerForScore } from '../../../utils/questionsAndAnswersHelpers'
import BaseOffCanvas from '../../common/BaseOffCanvas.vue'
import ActionCenterListView from './ActionCenterListView.vue'
import ActionCenterQuestionView from './ActionCenterQuestionView.vue'
import ActionCenterConfirmationView from './ActionCenterConfirmationView.vue'
import ActionCenterCompletedListView from './ActionCenterCompletedListView.vue'
import ActionCenterDetailView from './ActionCenterDetailView.vue'
import { parseContactEmails, formatRiskDollars } from '../helpers'
import { handleErrors } from '../../../utils/errorHandler'
import { ComplianceAuditLogService } from '../../../services/ComplianceAuditLogService'

const navigationStore = useNavigationStore()
const actionCenterStore = useActionCenterStore()
const emitter = inject('emitter', null)
const functionsBaseUrl = inject('functionsBaseUrl')
const { post, put } = RequestService()
const { recordAnswerChange, getAuditTrail } = ComplianceAuditLogService()

const offcanvasRef = ref(null)

const employerName = ref('')
const riskSummary = ref('')
const items = ref([])
const planYearId = ref(null)
const employerId = ref(null)

const currentView = ref('list') // 'list' | 'question' | 'confirmation' | 'completed-detail' | 'pending-detail'
const selectedActionItem = ref(null)

// Reactive variables for current question answer and notes
const currentAnswer = ref(null)
const currentNotes = ref('')
const currentAnswerId = ref(null)

// Confirmation view data
const riskPercent = ref('')
const riskDollars = ref('')
const completedRequirementTitle = ref('')
const riskPercentAfter = ref('')
const riskDollarsAfter = ref('')
const contactEmailsRaw = ref('')
const isSavingQuestion = ref(false)

const completedItems = ref([])
const isLoadingAuditTrail = ref(true)
const selectedCompletedItem = ref(null)
const selectedPendingItem = ref(null)
const projectedRiskScore = ref('--')
const projectedRiskAmount = ref('--')
const isCalculatingProjection = ref(false)
const totalRequirements = computed(() => items.value.length + completedItems.value.length)
const requirementsSummary = computed(() =>
  `${items.value.length} of ${totalRequirements.value} Requirements Need Attention`
)
const completedSummary = computed(() =>
  `${completedItems.value.length} of ${totalRequirements.value} Requirements Complete`
)

async function open(payload) {
  employerName.value = payload.employerName || ''
  riskSummary.value = `${payload.riskPercentLabel || '--'} Risk Score • ${payload.riskDollarsLabel || '--'} Risk $$`
  items.value = Array.isArray(payload.actionItems) ? payload.actionItems : []
  planYearId.value = payload.planYearId
  employerId.value = payload.employerId

  // Store risk values for confirmation view
  riskPercent.value = payload.riskPercentLabel && payload.riskPercentLabel !== '--' 
    ? `${Math.round(parseFloat(payload.riskPercentLabel.replace('%', '')))}%` 
    : '--'
  riskDollars.value = payload.riskDollarsLabel || '--'
  riskPercentAfter.value = riskPercent.value
  riskDollarsAfter.value = riskDollars.value
  contactEmailsRaw.value = payload.contactEmails || ''

  currentView.value = 'list'
  selectedActionItem.value = null

  isLoadingAuditTrail.value = true
  fetchCompletedItems(payload.planYearId)
  offcanvasRef.value?.show()
}

async function fetchCompletedItems(pyId) {
  try {
    isLoadingAuditTrail.value = true
    const records = await getAuditTrail(pyId)
    if (!records || records.length === 0) {
      completedItems.value = []
      return
    }

    // Build a set of currently-pending question IDs for cross-reference
    const pendingQuestionIds = new Set(
      items.value.map(item => String(item.questionId))
    )

    // Get the latest record per question_id (records are ordered by timestamp DESC)
    const latestByQuestion = {}
    for (const record of records) {
      if (!latestByQuestion[record.question_id]) {
        latestByQuestion[record.question_id] = record
      }
    }

    // Keep only items whose latest record is action_item_resolved
    // AND that are not currently in the pending list
    completedItems.value = Object.values(latestByQuestion)
      .filter(r => r.change_type === 'action_item_resolved'
        && !pendingQuestionIds.has(String(r.question_id)))
  } catch (err) {
    handleErrors('fetchCompletedItems', err)
    completedItems.value = []
  } finally {
    isLoadingAuditTrail.value = false
  }
}

function navigateToAssessment() {
  navigateToDetailEmployer(2)
}

function close() {
  currentView.value = 'list'
  offcanvasRef.value?.hide()
}

function openQuestionView(item) {
  selectedActionItem.value = item
  completedRequirementTitle.value = item.title || item.body || 'Requirement'
  // Find the current plan year in the store
  const currentPlanYear = actionCenterStore.accountOwnerPlanYears.find(py => 
    py.id === planYearId.value
  )
  // Find existing answer from the plan year's answers array
  const existingAnswer = currentPlanYear?.answers?.find(ans => 
    String(ans.question_id) === String(item.questionId)
  )
  
  // Keep the answer in its original type (string, number, array, etc.)
  if (existingAnswer?.answer !== undefined && existingAnswer?.answer !== null) {
    if (typeof existingAnswer.answer === 'string') {
      try {
        currentAnswer.value = JSON.parse(existingAnswer.answer)
      } catch {
        currentAnswer.value = existingAnswer.answer
      }
    } else {
      currentAnswer.value = existingAnswer.answer
    }
  } else {
    currentAnswer.value = null
  }
  currentNotes.value = existingAnswer?.notes || ''
  currentAnswerId.value = existingAnswer?.id || null
  
  currentView.value = 'question'
}

function backToList() {
  currentView.value = 'list'
  selectedActionItem.value = null
  if (planYearId.value) {
    fetchCompletedItems(planYearId.value)
  }
}

function openShareUpdateView() {
  // Emit global event to show ShareUpdateView in main ActionCenter
  if (emitter) {
    emitter.emit('action-center:start-share-update', {
      employerName: employerName.value,
      requirementTitle: completedRequirementTitle.value,
      riskScoreBefore: riskPercent.value,
      riskScoreAfter: riskPercentAfter.value,
      riskAmountBefore: riskDollars.value,
      riskAmountAfter: riskDollarsAfter.value,
      contactEmails: parseContactEmails(contactEmailsRaw.value)
    })
  }
  // Close the modal
  close()
}

function navigateToDetailEmployer(formToLoad) {
  const routes = navigationStore.customNavigation.routes
  const detailRoute = routes.DetailEmployer
  
  navigationStore.customNavigation.navigate({
    newPage: detailRoute.name,
    subPage: detailRoute.children[formToLoad].name,
    params: { planYearId: planYearId.value },
    query: { questionId: selectedActionItem.value?.questionId }
  })
  
  close()
}

function handleGoToReport() {
  // formToLoad = 3 for Gap Report (Pro Report)
  navigateToDetailEmployer(3)
}

function handleGoToAssessment() {
  // formToLoad = 2 for Assessment page
  navigateToDetailEmployer(2)
}

// ============ QUERIES ============

function getCurrentPlanYear() {
  return actionCenterStore.accountOwnerPlanYears.find(py => py.id === planYearId.value)
}

function buildAnswerPayload() {
  const answerValue = selectedActionItem.value?.type === 4 
    ? String(currentAnswer.value ?? '') 
    : JSON.stringify(currentAnswer.value)

  const payload = {
    question_id: String(selectedActionItem.value.questionId),
    employer_plan_years_id: String(planYearId.value),
    answer: answerValue,
    employerId: String(employerId.value),
    notes: currentNotes.value,
    note_edited: !!currentNotes.value,
  }

  if (currentAnswerId.value) {
    payload.answer_id = currentAnswerId.value
  }

  return payload
}

function getUpdatedActionItemIds(currentPlanYear, completedQuestionId) {
  const currentIds = currentPlanYear?.action_items_question_ids || []
  return currentIds.filter(id => String(id) !== String(completedQuestionId))
}

// ============ COMMANDS ============

async function saveAnswer(payload) {
  const url = `${functionsBaseUrl}/questionsGateway/api/v1/answers`
  await post('saveAnswers', url, [payload])
}

async function updateActionItemIds(updatedIds) {
  const url = `${functionsBaseUrl}/employerGateway/api/v1/employers/${employerId.value}`
  await put('updateEmployer', url, {
    id: employerId.value,
    complianceYearId: planYearId.value,
    action_items_question_ids: updatedIds
  })
}

function updateStoreAnswer(currentPlanYear, payload) {
  if (!currentPlanYear?.answers) return

  const existingIndex = currentPlanYear.answers.findIndex(
    ans => String(ans.question_id) === String(selectedActionItem.value.questionId)
  )

  if (existingIndex !== -1) {
    currentPlanYear.answers[existingIndex] = {
      ...currentPlanYear.answers[existingIndex],
      answer: payload.answer,
      notes: currentNotes.value,
      ...(payload.note_edited && { note_updated_at: new Date().toISOString() })
    }
  } else {
    currentPlanYear.answers.push({
      id: payload.answer_id || `${planYearId.value}_${selectedActionItem.value.questionId}`,
      ...payload
    })
  }
}

function updateLocalUIState(currentPlanYear, updatedIds, completedQuestionId) {
  if (currentPlanYear) {
    currentPlanYear.action_items_question_ids = updatedIds
    // Also update the action_items array so the table reflects the change
    if (Array.isArray(currentPlanYear.action_items)) {
      currentPlanYear.action_items = currentPlanYear.action_items.filter(
        item => String(item.questionId) !== String(completedQuestionId)
      )
    }
  }
  items.value = items.value.filter(
    item => String(item.questionId) !== String(completedQuestionId)
  )
}

/**
 * Updates the answersMap and assessmentQuestionsData in the plan year store
 * after an answer has been saved, so score calculations use fresh data.
 */
function updatePlanYearDataAfterAnswer(currentPlanYear, questionId, newAnswerValue) {
  if (!currentPlanYear) return
  
  // Update answersMap with the new answer
  if (currentPlanYear.answersMap) {
    currentPlanYear.answersMap[questionId] = normalizeAnswer(newAnswerValue)
  } else {
    // Build answersMap if it doesn't exist
    currentPlanYear.answersMap = buildAnswersMap(currentPlanYear.answers || [])
    currentPlanYear.answersMap[questionId] = normalizeAnswer(newAnswerValue)
  }
  
  // Update the answer in assessmentQuestionsData if it exists
  if (currentPlanYear.assessmentQuestionsData) {
    Object.values(currentPlanYear.assessmentQuestionsData).forEach(section => {
      const question = section.find(q => String(q.id) === String(questionId))
      if (question) {
        question.answer = normalizeAnswerForScore(newAnswerValue, question)
      }
    })
  }
}

async function recalculateRiskScores() {
  try {
    const currentPlanYear = getCurrentPlanYear()
    
    if (!currentPlanYear) {
      handleErrors('Plan year not found in store', new Error('Plan year not found in store'))
      return
    }
    
    // Convert Proxy objects to plain objects using toRaw (faster than JSON.parse/stringify)
    const plainAnswersMap = toRaw(currentPlanYear.answersMap) || {}
    const plainAssessmentQuestions = toRaw(currentPlanYear.assessmentQuestionsData) || {}
    
    // Build context from already-loaded Action Center data (no API calls needed)
    const context = {
      skipStores: true,
      assessmentQuestions: plainAssessmentQuestions,
      answersMap: plainAnswersMap,
      employer: {
        id: employerId.value,
        planYearId: planYearId.value,
        ...currentPlanYear.employer
      },
      planYearId: planYearId.value,
      functionsBaseUrl: functionsBaseUrl
    }
    
    // Use context-based service (no store loading)
    const { saveScore } = ScoreCalculationsService(context)
    
    // Single calculation + save, returns the calculated values
    const result = await saveScore()
    
    if (result) {
      // Update ActionCenterStore with the exact values saved to DB
      const planYearInStore = actionCenterStore.accountOwnerPlanYears.find(py => py.id === planYearId.value)
      if (planYearInStore) {
        planYearInStore.total_compliance_risk = result.totalComplianceRisk
        planYearInStore.percentage_compliance_risk = parseFloat(result.percentageComplianceRisk)
      }
      
      // Re-apply filters to propagate changes to filteredPlanYears
      actionCenterStore.applyFilters()

      // Use the exact values returned from saveScore (single source of truth)
      riskPercentAfter.value = `${Math.round(parseFloat(result.percentageComplianceRisk))}%`
      riskDollarsAfter.value = formatRiskDollars(result.totalComplianceRisk)
    }
  } catch (error) {
    handleErrors('Failed to recalculate risk:', error)
    riskPercentAfter.value = riskPercent.value
    riskDollarsAfter.value = riskDollars.value
  }
}

// ============ ORCHESTRATOR ============

async function handleQuestionComplete() {
  isSavingQuestion.value = true

  try {
    const completedQuestionId = selectedActionItem.value.questionId
    const currentPlanYear = getCurrentPlanYear()
    const answerPayload = buildAnswerPayload()

    const previousAnswer = currentAnswer.value
    const previousActionItemIds = currentPlanYear?.action_items_question_ids?.map(String) || []
    const complianceSnapshotBefore = {
      total_compliance_risk: currentPlanYear?.total_compliance_risk ?? null,
      percentage_compliance_risk: currentPlanYear?.percentage_compliance_risk ?? null
    }

    await saveAnswer(answerPayload)
    updateStoreAnswer(currentPlanYear, answerPayload)
    
    // Update the plan year data with the new answer for score calculations
    updatePlanYearDataAfterAnswer(currentPlanYear, completedQuestionId, answerPayload.answer)

    const updatedIds = getUpdatedActionItemIds(currentPlanYear, completedQuestionId)
    await updateActionItemIds(updatedIds)
    updateLocalUIState(currentPlanYear, updatedIds, completedQuestionId)

    await recalculateRiskScores()

    const complianceSnapshotAfter = {
      total_compliance_risk: parseFloat(riskDollarsAfter.value?.replace(/[^\d.-]/g, '')) || null,
      percentage_compliance_risk: parseFloat(riskPercentAfter.value?.replace('%', '')) || null
    }
    const planYearAfterRecalc = getCurrentPlanYear()
    if (planYearAfterRecalc) {
      complianceSnapshotAfter.total_compliance_risk = planYearAfterRecalc.total_compliance_risk ?? complianceSnapshotAfter.total_compliance_risk
      complianceSnapshotAfter.percentage_compliance_risk = planYearAfterRecalc.percentage_compliance_risk ?? complianceSnapshotAfter.percentage_compliance_risk
    }

    recordAnswerChange({
      employerId: employerId.value,
      planYearId: planYearId.value,
      agencyId: currentPlanYear?.employer?.agency_id || null,
      questionId: String(completedQuestionId),
      source: 'action_center',
      changeType: 'action_item_resolved',
      previousAnswer: previousAnswer ?? null,
      newAnswer: answerPayload.answer ?? null,
      previousActionItemIds,
      newActionItemIds: updatedIds.map(String),
      complianceSnapshotBefore,
      complianceSnapshotAfter
    }).catch(err => handleErrors('Compliance audit log failed:', err))

    currentView.value = 'confirmation'
    
    // Emit scroll event to highlight updated row in table
    if (emitter && planYearId.value) {
      emitter.emit('action-center:scroll-to-employer', { planYearId: planYearId.value })
    }
  } catch (error) {
    handleErrors('Failed to save answer:', error)
  } finally {
    isSavingQuestion.value = false
  }
}

function handleCompletedItemClick(item) {
  selectedCompletedItem.value = item
  currentView.value = 'completed-detail'
}

function backToListFromDetail() {
  currentView.value = 'list'
  selectedCompletedItem.value = null
}

function handleTitleClick(item) {
  selectedPendingItem.value = item
  currentView.value = 'pending-detail'
  calculateProjectedRisk(item)
}

function backToListFromPendingDetail() {
  currentView.value = 'list'
  selectedPendingItem.value = null
  projectedRiskScore.value = '--'
  projectedRiskAmount.value = '--'
}

function goToMarkCompleteFromPending() {
  const item = selectedPendingItem.value
  selectedPendingItem.value = null
  projectedRiskScore.value = '--'
  projectedRiskAmount.value = '--'
  if (item) {
    openQuestionView(item)
  }
}

/**
 * Finds a hypothetical correct answer for a question that currently has a wrong answer.
 * Used for dry-run score projection.
 */
function getProjectedCorrectAnswer(item) {
  if (!item) return null

  const wrongAnswer = item.wrongAnswer
  const wrongValues = Array.isArray(wrongAnswer?.values) ? wrongAnswer.values : []

  if (wrongValues.length === 0) return null

  let currentAns = item.answer
  if (typeof currentAns === 'string') {
    try { currentAns = JSON.parse(currentAns) } catch { /* keep */ }
  }

  // For array answers: remove wrong values
  if (Array.isArray(currentAns)) {
    const filtered = currentAns.filter(v => !wrongValues.includes(v))
    return filtered
  }

  // For scalar answers: find first valid (non-wrong) option index
  let optionsCount = 0
  try {
    const opts = typeof item.options === 'string' ? JSON.parse(item.options) : item.options
    optionsCount = Array.isArray(opts?.values) ? opts.values.length : 0
  } catch { optionsCount = 0 }

  for (let i = 0; i < optionsCount; i++) {
    if (!wrongValues.includes(i)) return i
  }

  return null
}

/**
 * Calculates projected risk scores if this action item were resolved (dry-run, no DB save).
 */
function calculateProjectedRisk(item) {
  isCalculatingProjection.value = true

  try {
    const currentPlanYear = getCurrentPlanYear()
    if (!currentPlanYear?.assessmentQuestionsData || !currentPlanYear?.answersMap) {
      isCalculatingProjection.value = false
      return
    }

    const questionId = String(item.questionId)
    const correctAnswer = getProjectedCorrectAnswer(item)

    // Deep clone answersMap and assessmentQuestionsData
    const clonedAnswersMap = JSON.parse(JSON.stringify(toRaw(currentPlanYear.answersMap)))
    const clonedAssessmentQuestions = JSON.parse(JSON.stringify(toRaw(currentPlanYear.assessmentQuestionsData)))

    // Update the answer in both cloned structures
    clonedAnswersMap[questionId] = correctAnswer

    Object.values(clonedAssessmentQuestions).forEach(section => {
      const question = section.find(q => String(q.id) === questionId)
      if (question) {
        question.answer = correctAnswer
      }
    })

    // Create a dry-run ScoreCalculationsService with the modified data
    const context = {
      skipStores: true,
      assessmentQuestions: clonedAssessmentQuestions,
      answersMap: clonedAnswersMap,
      employer: {
        id: employerId.value,
        planYearId: planYearId.value,
        ...currentPlanYear.employer
      },
      planYearId: planYearId.value,
      functionsBaseUrl: functionsBaseUrl
    }

    const { calculateScore } = ScoreCalculationsService(context)
    const result = calculateScore()

    projectedRiskScore.value = `${Math.round(parseFloat(result.percentageComplianceRisk))}%`
    projectedRiskAmount.value = formatRiskDollars(result.totalComplianceRisk)
  } catch (error) {
    handleErrors('Failed to calculate projected risk:', error)
    projectedRiskScore.value = '--'
    projectedRiskAmount.value = '--'
  } finally {
    isCalculatingProjection.value = false
  }
}

defineExpose({ open, close })
</script>

<template>
  <BaseOffCanvas
    ref="offcanvasRef"
    canvas-id="actionItemsId"
    canvas-position="end"
    class="action-center-offcanvas"
  >
    <template #header>
      <span class="header-22">
        <strong>{{ employerName + (employerName.length > 25 ? '' : ' Action Items') }}</strong>
      </span>
    </template>

    <div class="pb-4">
      <transition name="fade" mode="out-in">
        <div v-if="currentView === 'list'" key="list-view">
          <ActionCenterListView
            :requirements-summary="requirementsSummary"
            :items="items"
            @item-click="openQuestionView"
            @title-click="handleTitleClick"
          />
          <ActionCenterCompletedListView
            :completed-summary="completedSummary"
            :completed-items="completedItems"
            :loading="isLoadingAuditTrail"
            @item-click="handleCompletedItemClick"
            @navigate-to-assessment="navigateToAssessment"
          />
        </div>

        <div v-else-if="currentView === 'question'" key="question-view">
          <ActionCenterQuestionView
            :action-item="selectedActionItem"
            :answers-map="getCurrentPlanYear()?.answersMap || {}"
            :is-saving="isSavingQuestion"
            v-model:answer="currentAnswer"
            v-model:notes="currentNotes"
            @back="backToList"
            @complete="handleQuestionComplete"
          />
        </div>

        <div v-else-if="currentView === 'completed-detail'" key="completed-detail-view">
          <ActionCenterDetailView
            :item="selectedCompletedItem"
            mode="completed"
            :employer-name="employerName"
            :risk-score-before="riskPercent"
            :risk-amount-before="riskDollars"
            :plan-start-date="getCurrentPlanYear()?.plan_start_date || null"
            @back="backToListFromDetail"
            @close-panel="close"
          />
        </div>

        <div v-else-if="currentView === 'pending-detail'" key="pending-detail-view">
          <ActionCenterDetailView
            :item="selectedPendingItem"
            mode="pending"
            :employer-name="employerName"
            :current-risk-score="riskPercent"
            :current-risk-amount="riskDollars"
            :projected-risk-score="projectedRiskScore"
            :projected-risk-amount="projectedRiskAmount"
            :is-calculating-projection="isCalculatingProjection"
            :plan-start-date="getCurrentPlanYear()?.plan_start_date || null"
            @back="backToListFromPendingDetail"
            @go-to-mark-complete="goToMarkCompleteFromPending"
          />
        </div>

        <div v-else-if="currentView === 'confirmation'" key="confirmation-view">
          <ActionCenterConfirmationView
            :requirement-title="completedRequirementTitle"
            :risk-score-before="riskPercent"
            :risk-score-after="riskPercentAfter"
            :risk-amount-before="riskDollars"
            :risk-amount-after="riskDollarsAfter"
            :employer-name="employerName"
            assessment-update-text="Questions on the Assessment page have been updated to reflect the completed requirement."
            @back-to-list="backToList"
            @go-to-report="handleGoToReport"
            @go-to-assessment="handleGoToAssessment"
            @share-update="openShareUpdateView"
          />
        </div>
      </transition>
    </div>
  </BaseOffCanvas>
</template>

<style scoped lang="scss">
.action-center-offcanvas :deep(.offcanvas-header) {
  border-bottom: 1px solid var(--bs-border-color);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
